import { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './MultiSelectDropdown.module.css';
import { ChevronIcon } from './selectIcons';

interface MultiSelectDropdownProps {
  label: string;
  options: { value: string; label: string; divider?: boolean }[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  className?: string;
  autoSelectedValues?: string[]; // 自動適用されたフィルタ値（読み取り専用で表示）
  dropdownWidth?: {
    min?: string;
    max?: string;
  };
  /** ドロップダウンメニュー（ポータル内）に追加するクラス名 */
  dropdownClassName?: string;
  /** テキスト幅にフィットさせ、ビューポート内に収める */
  fitContent?: boolean;
}

export function MultiSelectDropdown({
  label,
  options,
  selectedValues,
  onChange,
  className,
  autoSelectedValues,
  dropdownWidth,
  dropdownClassName,
  fitContent,
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const useFitContent = fitContent || false;

  const updateDropdownPosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, []);

  // fitContent時: レンダリング後に実際の幅を計測して再配置
  useLayoutEffect(() => {
    if (!isOpen || !useFitContent || !dropdownRef.current || !buttonRef.current) return;

    const margin = 16;
    const viewportWidth = window.innerWidth;
    const maxAvailable = viewportWidth - margin * 2;
    const el = dropdownRef.current;
    const rect = buttonRef.current.getBoundingClientRect();

    // box-sizing:border-box では width に border/スクロールバー分が含まれるが
    // scrollWidth は content+padding のみ。差分を足さないと中身が右端で欠ける。
    const borderAndScrollbar = el.offsetWidth - el.clientWidth;
    const naturalWidth = Math.min(el.scrollWidth + borderAndScrollbar, maxAvailable);
    el.style.width = `${naturalWidth}px`;

    const buttonCenter = rect.left + rect.width / 2;
    let left = buttonCenter - naturalWidth / 2;
    left = Math.max(margin, Math.min(left, viewportWidth - naturalWidth - margin));
    el.style.left = `${left}px`;
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        containerRef.current && !containerRef.current.contains(target) &&
        dropdownRef.current && !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      updateDropdownPosition();
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updateDropdownPosition, true);
      window.addEventListener('resize', updateDropdownPosition);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        window.removeEventListener('scroll', updateDropdownPosition, true);
        window.removeEventListener('resize', updateDropdownPosition);
      };
    }
  }, [isOpen, updateDropdownPosition]);

  const toggleValue = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  // 表示用の全選択値（ユーザー選択 + 自動選択）
  const allSelectedValues = autoSelectedValues
    ? [...new Set([...autoSelectedValues, ...selectedValues])]
    : selectedValues;

  const displayText = allSelectedValues.length === 0
    ? label
    : autoSelectedValues && autoSelectedValues.length > 0
    ? `${label}: ${allSelectedValues.length}件選択中（自動${autoSelectedValues.length}件）`
    : `${label}: ${allSelectedValues.length}件選択中`;

  return (
    <div ref={containerRef} className={`${styles.container} ${className || ''}`}>
      <button
        ref={buttonRef}
        type="button"
        className={`${styles.button} ${allSelectedValues.length > 0 ? styles.active : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{displayText}</span>
        <span className={styles.arrow}><ChevronIcon open={isOpen} /></span>
      </button>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className={`modal-surface ${styles.dropdown} ${dropdownClassName || ''}`}
          style={{
            position: 'fixed',
            top: dropdownPosition.top,
            left: dropdownPosition.left,
            width: useFitContent ? 'max-content' : undefined,
            minWidth: useFitContent ? undefined : (dropdownWidth?.min || dropdownPosition.width),
            maxWidth: useFitContent ? 'calc(100vw - 32px)' : dropdownWidth?.max,
            '--dropdown-top': `${dropdownPosition.top}px`,
          } as React.CSSProperties}
        >
          {/* すべて選択/解除ボタン */}
          <div className={styles.actions}>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                const nonAutoValues = options
                  .map(opt => opt.value)
                  .filter(val => !autoSelectedValues?.includes(val));
                onChange(nonAutoValues);
              }}
              className={styles.actionButton}
            >
              すべて選択
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange([]);
              }}
              className={styles.actionButton}
            >
              クリア
            </button>
          </div>
          {options.map((option) => {
            const isAutoSelected = autoSelectedValues?.includes(option.value);
            const isChecked = allSelectedValues.includes(option.value);

            return (
              <label
                key={option.value}
                className={styles.option}
                style={option.divider ? { borderTop: '1px solid rgba(128, 128, 128, 0.2)', marginTop: '4px', paddingTop: '8px' } : undefined}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleValue(option.value)}
                  className={styles.checkbox}
                />
                <span>
                  {option.label}
                  {isAutoSelected && (
                    <span className={styles.autoLabel}>
                      (自動)
                    </span>
                  )}
                </span>
              </label>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
